import { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Package,
    AlertCircle,
    Filter,
    X,
    Grid3X3,
    LayoutGrid,
    SlidersHorizontal,
    Truck,
    Banknote,
    Shield,
    Star,
    CheckCircle2,
    ArrowUpDown
} from 'lucide-react';
import { ProductCard, ProductCardSkeleton } from '../components/product/ProductCard';
import { Pagination } from '../components/ui/Pagination';
import { productsApi } from '../lib/api';
import { scrollToTop, cn } from '../lib/utils';

const PRODUCTS_PER_PAGE = 20;

// Sort options
const SORT_OPTIONS = [
    { value: 'newest', label: 'Plus récents' },
    { value: 'price-asc', label: 'Prix croissant' },
    { value: 'price-desc', label: 'Prix décroissant' },
    { value: 'popular', label: 'Populaires' },
];

interface Category {
    id: string;
    name: string;
    slug: string;
    count: number;
}

export function ShopPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const currentPage = parseInt(searchParams.get('page') || '1', 10);
    const currentCategory = searchParams.get('category');
    const currentSort = searchParams.get('sort') || 'newest';
    const searchQuery = searchParams.get('search') || '';

    const [isMobileFilterOpen, setIsMobileFilterOpen] = useState(false);
    const [gridCols, setGridCols] = useState<3 | 4>(4);

    // Fetch categories
    const { data: categories = [] } = useQuery({
        queryKey: ['store-categories'],
        queryFn: productsApi.getCategories,
        staleTime: 1000 * 60 * 10,
    });

    // Fetch products
    const { data, isLoading, isError, error, refetch } = useQuery({
        queryKey: ['products', currentPage, currentCategory, currentSort, searchQuery],
        queryFn: () => productsApi.getAll({
            page: currentPage,
            limit: PRODUCTS_PER_PAGE,
            category: currentCategory === 'all' ? undefined : currentCategory || undefined,
            search: searchQuery || undefined,
        }),
        staleTime: 1000 * 60 * 5,
    });

    const products = data?.data || [];
    const totalPages = data?.meta?.totalPages || 1;
    const totalProducts = data?.meta?.total || 0;

    // Random stats for social proof
    const stats = useMemo(() => ({
        orders: Math.floor(Math.random() * 100) + 200,
        rating: 4.8,
    }), []);

    // Handle filter changes
    const updateFilter = (key: string, value: string | null) => {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }
        if (key !== 'page') {
            newParams.set('page', '1');
        }
        setSearchParams(newParams);
        setIsMobileFilterOpen(false);
    };

    // Scroll to top on page/filter change
    useEffect(() => {
        scrollToTop();
    }, [currentPage, currentCategory, currentSort]);

    return (
        <div className="min-h-screen bg-gray-50 pb-20 md:pb-0">
            {/* Hero Banner */}
            <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white">
                <div className="container-custom py-6 md:py-12">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
                        <div>
                            <h1 className="text-xl md:text-3xl lg:text-4xl font-display font-bold mb-1 md:mb-2">
                                {currentCategory && currentCategory !== 'all' 
                                    ? currentCategory 
                                    : searchQuery 
                                        ? `Résultats pour "${searchQuery}"`
                                        : 'Tous nos Produits'}
                            </h1>
                            <p className="text-sm md:text-base text-gray-400">
                                {totalProducts} produits • 58 wilayas
                            </p>
                        </div>
                        
                        {/* Trust badges */}
                        <div className="flex items-center gap-4">
                            <div className="hidden md:flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl">
                                <Banknote className="w-5 h-5 text-green-400" />
                                <span className="text-sm font-medium">Paiement à la livraison</span>
                            </div>
                            <div className="hidden md:flex items-center gap-2 bg-white/10 px-4 py-2 rounded-xl">
                                <Star className="w-5 h-5 text-amber-400 fill-amber-400" />
                                <span className="text-sm font-medium">{stats.rating}/5 ({stats.orders}+ avis)</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Trust Bar */}
            <div className="md:hidden bg-white border-b border-gray-100 py-2.5">
                <div className="container-custom">
                    <div className="flex items-center justify-center gap-4 text-xs">
                        <span className="flex items-center gap-1 text-green-600">
                            <Banknote className="w-4 h-4" />
                            COD
                        </span>
                        <span className="flex items-center gap-1 text-blue-600">
                            <Truck className="w-4 h-4" />
                            58 wilayas
                        </span>
                        <span className="flex items-center gap-1 text-purple-600">
                            <Shield className="w-4 h-4" />
                            Garanti
                        </span>
                    </div>
                </div>
            </div>

            {/* Mobile Category Pills - Horizontal Scroll */}
            <div className="lg:hidden bg-white border-b border-gray-100 py-3 overflow-hidden">
                <div className="flex gap-2 px-4 overflow-x-auto scrollbar-hide touch-pan-x">
                    <button
                        onClick={() => updateFilter('category', 'all')}
                        className={cn(
                            'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all touch-manipulation',
                            (!currentCategory || currentCategory === 'all')
                                ? 'bg-primary-500 text-white shadow-md'
                                : 'bg-gray-100 text-gray-700 active:bg-gray-200'
                        )}
                    >
                        Tous
                    </button>
                    {(categories as Category[]).map((cat) => (
                        <button
                            key={cat.id}
                            onClick={() => updateFilter('category', cat.name)}
                            className={cn(
                                'flex-shrink-0 px-4 py-2 rounded-full text-sm font-medium transition-all touch-manipulation whitespace-nowrap',
                                currentCategory === cat.name
                                    ? 'bg-primary-500 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 active:bg-gray-200'
                            )}
                        >
                            {cat.name}
                        </button>
                    ))}
                </div>
            </div>

            <div className="container-custom py-6 md:py-8">
                <div className="flex gap-8">
                    {/* Desktop Sidebar */}
                    <aside className="hidden lg:block w-64 flex-shrink-0">
                        <div className="sticky top-24 space-y-6">
                            {/* Categories */}
                            <div className="bg-white rounded-2xl p-5 shadow-sm border border-gray-100">
                                <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                                    <Filter className="w-4 h-4" />
                                    Catégories
                                </h3>
                                <div className="space-y-1">
                                    <button
                                        onClick={() => updateFilter('category', 'all')}
                                        className={cn(
                                            'w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between',
                                            (!currentCategory || currentCategory === 'all')
                                                ? 'bg-primary-50 text-primary-600 font-medium'
                                                : 'text-gray-600 hover:bg-gray-50'
                                        )}
                                    >
                                        <span>Tous les produits</span>
                                        {(!currentCategory || currentCategory === 'all') && (
                                            <CheckCircle2 className="w-4 h-4" />
                                        )}
                                    </button>
                                    {(categories as Category[]).map((cat) => (
                                        <button
                                            key={cat.id}
                                            onClick={() => updateFilter('category', cat.name)}
                                            className={cn(
                                                'w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center justify-between',
                                                currentCategory === cat.name
                                                    ? 'bg-primary-50 text-primary-600 font-medium'
                                                    : 'text-gray-600 hover:bg-gray-50'
                                            )}
                                        >
                                            <span>{cat.name}</span>
                                            <span className="text-xs text-gray-400">({cat.count})</span>
                                        </button>
                                    ))}
                                </div>
                            </div>

                            {/* COD Info Card */}
                            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-2xl p-5 text-white">
                                <Banknote className="w-8 h-8 mb-3" />
                                <h4 className="font-bold mb-2">Paiement à la Livraison</h4>
                                <p className="text-sm text-green-100">
                                    Payez en espèces quand vous recevez votre commande. Aucun paiement en ligne requis.
                                </p>
                            </div>
                        </div>
                    </aside>

                    {/* Main Content */}
                    <div className="flex-1 min-w-0">
                        {/* Toolbar */}
                        <div className="bg-white rounded-xl p-4 mb-6 shadow-sm border border-gray-100">
                            <div className="flex flex-wrap items-center justify-between gap-4">
                                {/* Left - Results count */}
                                <div className="flex items-center gap-3">
                                    <span className="text-sm text-gray-600">
                                        <strong className="text-gray-900">{totalProducts}</strong> produits
                                    </span>
                                    {currentCategory && currentCategory !== 'all' && (
                                        <button
                                            onClick={() => updateFilter('category', null)}
                                            className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600 px-2 py-1 rounded-full hover:bg-gray-200"
                                        >
                                            {currentCategory}
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>

                                {/* Right - Controls */}
                                <div className="flex items-center gap-3">
                                    {/* Mobile Filter Button */}
                                    <button
                                        onClick={() => setIsMobileFilterOpen(true)}
                                        className="lg:hidden flex items-center gap-2 px-4 py-2.5 bg-gray-100 rounded-xl text-sm font-medium text-gray-700 active:bg-gray-200 transition-colors touch-manipulation"
                                    >
                                        <SlidersHorizontal className="w-4 h-4" />
                                        Filtres
                                    </button>

                                    {/* Sort Dropdown */}
                                    <div className="relative">
                                        <select
                                            value={currentSort}
                                            onChange={(e) => updateFilter('sort', e.target.value)}
                                            className="appearance-none bg-gray-100 border-0 rounded-lg px-4 py-2 pr-10 text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-200 transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500"
                                        >
                                            {SORT_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>
                                                    {opt.label}
                                                </option>
                                            ))}
                                        </select>
                                        <ArrowUpDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500 pointer-events-none" />
                                    </div>

                                    {/* Grid Toggle - Desktop */}
                                    <div className="hidden md:flex items-center bg-gray-100 rounded-lg p-1">
                                        <button
                                            onClick={() => setGridCols(3)}
                                            className={cn(
                                                'p-2 rounded-md transition-colors',
                                                gridCols === 3 ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                                            )}
                                        >
                                            <Grid3X3 className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => setGridCols(4)}
                                            className={cn(
                                                'p-2 rounded-md transition-colors',
                                                gridCols === 4 ? 'bg-white shadow-sm' : 'hover:bg-gray-200'
                                            )}
                                        >
                                            <LayoutGrid className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Error State */}
                        {isError && (
                            <div className="text-center py-12 bg-red-50 rounded-2xl border border-red-100 mb-6">
                                <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                                <p className="text-red-600 font-medium mb-4">
                                    {error instanceof Error ? error.message : 'Erreur de chargement'}
                                </p>
                                <button onClick={() => refetch()} className="btn-primary">
                                    Réessayer
                                </button>
                            </div>
                        )}

                        {/* Loading State */}
                        {isLoading && (
                            <div className={cn(
                                'grid gap-4 md:gap-6',
                                gridCols === 3 
                                    ? 'grid-cols-2 md:grid-cols-3' 
                                    : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                            )}>
                                {Array.from({ length: 12 }).map((_, i) => (
                                    <ProductCardSkeleton key={i} />
                                ))}
                            </div>
                        )}

                        {/* Products Grid */}
                        {!isLoading && !isError && products.length > 0 && (
                            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                                <div className={cn(
                                    'grid gap-4 md:gap-6',
                                    gridCols === 3 
                                        ? 'grid-cols-2 md:grid-cols-3' 
                                        : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
                                )}>
                                    {products.map((product: any, index: number) => (
                                        <ProductCard key={product.id} product={product} index={index} />
                                    ))}
                                </div>

                                {/* Pagination */}
                                {totalPages > 1 && (
                                    <div className="mt-10 flex justify-center">
                                        <Pagination
                                            currentPage={currentPage}
                                            totalPages={totalPages}
                                            onPageChange={(page) => updateFilter('page', page.toString())}
                                        />
                                    </div>
                                )}
                            </motion.div>
                        )}

                        {/* Empty State */}
                        {!isLoading && !isError && products.length === 0 && (
                            <div className="text-center py-16 bg-white rounded-2xl border border-gray-100">
                                <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                                <h3 className="font-bold text-xl text-gray-900 mb-2">Aucun produit trouvé</h3>
                                <p className="text-gray-500 mb-6">Essayez de modifier vos filtres</p>
                                <button
                                    onClick={() => {
                                        const newParams = new URLSearchParams();
                                        setSearchParams(newParams);
                                    }}
                                    className="btn-primary"
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Mobile Filter Bottom Sheet */}
            <AnimatePresence>
                {isMobileFilterOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/60 z-40 lg:hidden"
                            onClick={() => setIsMobileFilterOpen(false)}
                        />
                        <motion.div
                            initial={{ y: '100%' }}
                            animate={{ y: 0 }}
                            exit={{ y: '100%' }}
                            transition={{ type: 'spring', damping: 28, stiffness: 300 }}
                            className="fixed bottom-0 left-0 right-0 bg-white z-50 lg:hidden shadow-2xl rounded-t-3xl max-h-[85vh] flex flex-col safe-area-bottom"
                        >
                            {/* Handle */}
                            <div className="flex justify-center pt-3 pb-2">
                                <div className="w-10 h-1 bg-gray-300 rounded-full" />
                            </div>

                            {/* Header */}
                            <div className="flex items-center justify-between px-5 pb-4 border-b">
                                <h3 className="font-bold text-lg">Filtres & Tri</h3>
                                <button
                                    onClick={() => setIsMobileFilterOpen(false)}
                                    className="p-2 -mr-2 active:bg-gray-100 rounded-xl touch-manipulation"
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Content */}
                            <div className="flex-1 overflow-y-auto overscroll-contain">
                                {/* Sort Options */}
                                <div className="p-5 border-b">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                        <ArrowUpDown className="w-4 h-4" />
                                        Trier par
                                    </h4>
                                    <div className="grid grid-cols-2 gap-2">
                                        {SORT_OPTIONS.map((opt) => (
                                            <button
                                                key={opt.value}
                                                onClick={() => updateFilter('sort', opt.value)}
                                                className={cn(
                                                    'px-4 py-3 rounded-xl text-sm transition-all touch-manipulation',
                                                    currentSort === opt.value
                                                        ? 'bg-primary-500 text-white font-medium shadow-md'
                                                        : 'bg-gray-100 text-gray-700 active:bg-gray-200'
                                                )}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Categories */}
                                <div className="p-5">
                                    <h4 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                                        <Filter className="w-4 h-4" />
                                        Catégories
                                    </h4>
                                    <div className="space-y-1.5">
                                        <button
                                            onClick={() => updateFilter('category', 'all')}
                                            className={cn(
                                                'w-full text-left px-4 py-3.5 rounded-xl text-sm transition-all flex items-center justify-between touch-manipulation',
                                                (!currentCategory || currentCategory === 'all')
                                                    ? 'bg-primary-50 text-primary-600 font-medium border-2 border-primary-200'
                                                    : 'bg-gray-50 text-gray-700 active:bg-gray-100'
                                            )}
                                        >
                                            <span>Tous les produits</span>
                                            {(!currentCategory || currentCategory === 'all') && (
                                                <CheckCircle2 className="w-5 h-5" />
                                            )}
                                        </button>
                                        {(categories as Category[]).map((cat) => (
                                            <button
                                                key={cat.id}
                                                onClick={() => updateFilter('category', cat.name)}
                                                className={cn(
                                                    'w-full text-left px-4 py-3.5 rounded-xl text-sm transition-all flex items-center justify-between touch-manipulation',
                                                    currentCategory === cat.name
                                                        ? 'bg-primary-50 text-primary-600 font-medium border-2 border-primary-200'
                                                        : 'bg-gray-50 text-gray-700 active:bg-gray-100'
                                                )}
                                            >
                                                <span>{cat.name}</span>
                                                <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{cat.count}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="p-5 border-t bg-gray-50">
                                <button
                                    onClick={() => setIsMobileFilterOpen(false)}
                                    className="w-full bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white py-4 rounded-xl font-semibold text-base shadow-lg touch-manipulation transition-colors"
                                >
                                    Voir {totalProducts} produits
                                </button>
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </div>
    );
}
